import React from 'react';
import { Link } from 'react-router-dom';
import { formatDuration } from '../../../utils/formatDuration';
import { pluralRu, EVENT_FORMS } from '../../../utils/pluralRu';
import {
  WEEKDAY_LABELS, buildEventsDrilldownUrl, buildRuleDrilldown, describePatternRecurrence,
} from '../summaryFormatters';
import { INSIGHT_GROUP_TITLES } from '../insightModel';

/**
 * ТЗ №1 §8.3 + дополнение §3: common_weekday/common_hour — локальное время
 * камеры pattern'а; при per_camera scope подписываем её timezone.
 * CTA — «Посмотреть связанные события», а не «ровно N» (§10.4).
 *
 * ТЗ 58 №15: группа внутри «На что обратить внимание», одна карточка на
 * каждый элемент repeated_patterns[]; attention[repeated_violation_pattern]
 * отдельно не выводится (см. insightModel). Без паттернов блок не рисуется.
 *
 * BM-FE-TZ-002C: drill-down — события правила карточки за весь период
 * вкладки (period). На «Нарушениях» это список нарушений, на
 * «Бизнес-метриках» — бизнес-события (onOpenBusinessEvents), не нарушения.
 */
const RepeatedPatterns = ({
  patterns, cameras, timezoneMode, period, kind = 'violation', onOpenBusinessEvents,
}) => {
  const isBusiness = kind === 'business';
  const tzByCamera = Object.fromEntries((cameras || []).map((camera) => [camera.camera_id, camera.display_timezone]));
  const items = Array.isArray(patterns) ? patterns : [];
  if (items.length === 0) return null;
  return (
    <div className="sum-insight-group">
      <div className="sum-card-head">
        {isBusiness ? (
          <h2 className="sum-card-title">Повторяющиеся бизнес-события</h2>
        ) : (
          <h3 className="sum-insight-group-title">{INSIGHT_GROUP_TITLES.attention.recurrence}</h3>
        )}
        {!isBusiness && <Link className="sum-link" to="/violations">Все нарушения</Link>}
      </div>
      <div className="sum-patterns">
        {items.map((pattern, index) => {
          const drilldown = buildRuleDrilldown(pattern, period);
          const href = !isBusiness && drilldown ? buildEventsDrilldownUrl(drilldown) : null;
          const tz = timezoneMode === 'per_camera' ? tzByCamera[pattern.camera_id] : '';
          const hasCommon = pattern.common_weekday != null && pattern.common_hour != null;
          const recurrence = describePatternRecurrence(pattern);
          const title = [pattern.rule_name, pattern.camera_name].filter(Boolean).join(' — ');
          return (
            <div className="sum-pattern" key={`${pattern.rule_id}-${pattern.camera_id}-${pattern.zone_id || ''}-${index}`}>
              <div className="sum-pattern-title">{title}</div>
              {pattern.object_name && <div className="sum-pattern-scope">{pattern.object_name}</div>}
              <div className="sum-pattern-zone">{pattern.zone_name || 'Общая область контроля'}</div>
              <div className="sum-pattern-count">
                <b>{pattern.violations_count}</b> {pluralRu(pattern.violations_count, EVENT_FORMS)} · {formatDuration(pattern.violation_duration_seconds)}
              </div>
              {hasCommon && (
                <div className="sum-pattern-common">
                  Чаще всего {WEEKDAY_LABELS[pattern.common_weekday]}, {String(pattern.common_hour).padStart(2, '0')}:00{tz ? ` (${tz})` : ''}
                </div>
              )}
              {recurrence && <div className="sum-pattern-recurrence">{recurrence}</div>}
              {href && <Link className="sum-link" to={href}>Посмотреть связанные события</Link>}
              {isBusiness && drilldown && onOpenBusinessEvents && (
                <button
                  type="button"
                  className="sum-link sum-link--button"
                  onClick={() => onOpenBusinessEvents({
                    ruleId: drilldown.rule_id,
                    cameraId: drilldown.camera_id,
                    from: drilldown.from,
                    to: drilldown.to,
                    ruleName: pattern.rule_name,
                    cameraName: pattern.camera_name,
                  })}
                >
                  Посмотреть связанные события
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RepeatedPatterns;
